/**
 * LodEditLayers — player edits, propagated up the LOD hierarchy.
 *
 * Coarse chunks are pure functions of the seed (`generateChunkLOD` samples the terrain fields on a
 * decimated lattice; it never reads lod-0 voxels), so a build committed at lod 0 was invisible the
 * moment the far view took over the volume. This module is the missing half: per coarse chunk it
 * keeps a sparse EDIT LAYER — voxel index → packed word — which `apply` merges into the freshly
 * generated data at ingest. Generation itself is untouched, so every terrain checksum guard holds.
 *
 * THE ONE RULE THAT MAKES DEEP LEVELS CORRECT: a level is always refolded from the STORED LAYER
 * IMMEDIATELY BELOW IT, never from the edit. Each layer is therefore the accumulation of everything
 * built underneath it, and a new edit merges into it by RECOMPUTING its box from the eight children
 * rather than max-ing into what was there. Both halves matter:
 *  - Folding a high level straight from the edited fine chunk would write that voxel with only the
 *    newest build's contribution, erasing every other build sharing the footprint (a lod-5 voxel
 *    spans 32768 fine voxels — that is a whole village).
 *  - Max-merging into the parent instead of recomputing could never LOWER a voxel, so a demolition
 *    or an undo would leave a permanent ghost at range, compounding per level.
 * Recompute-from-below leaves every entry a pure function of the current data in its footprint:
 * history-free, order-independent, and repairable by refolding any box. That is what makes the
 * debounce below safe at any delay.
 *
 * The KNOWN invariant (see foldDense.ts) is what keeps accumulation honest across separate edits:
 * a voxel is written only when all eight of its children have entries, so "a parent entry exists ⟹
 * its children all have entries" — a later fold can never half-inform a voxel and drop an older
 * build. Boxes are padded two voxels per level so anything ADJACENT to a change is refolded too,
 * which is what lets the may-lower erosion react to a neighbour turning solid.
 *
 * Cost per edit is ~1.14 passes over the edit box for ALL levels combined (the box shrinks 8x per
 * level), i.e. an order of magnitude under the SDF rasterization of the same build — so this runs on
 * the main thread, coalesced behind a long debounce. The expensive part of an edit at range is the
 * coarse REMESH, which is why the owner only remeshes what is on screen (`onLayersChanged`).
 */

import {
  LAYER_MAY_LOWER, MAX_PACKABLE_LOD, erodeMayLower, foldOctant, isEmptyBox, mergeLayerWord,
  raiseLayerLight, packChunkKeyLod,
  packChunkKey, packColumn, padBox, unionBox, unpackChunkKeyLod, unpackChunkKeyTuple,
  unpackColumn, voxelIndex, CHUNK_SIZE, WEIGHT_MASK, WEIGHT_SHIFT,
  type ChunkCoordLod, type FoldSink, type VoxelBox,
} from '@worldify/shared';

/** One coarse chunk's layer: voxel index → packed word (may carry LAYER_MAY_LOWER). */
export type EditLayer = Map<number, number>;

/**
 * A coarse chunk whose layer just changed. `lowered` means some voxel's floor DROPPED (a demolition,
 * an undo, or a carve that earned its may-lower flag) — a resident chunk cannot absorb that by
 * merging, because the procedural values it would have to return to are gone, so its owner has to
 * regenerate it rather than patch it.
 */
export type LodEditTouch = ChunkCoordLod & { lowered: boolean };

/** A vertical clip widening for one coarse column, in that level's chunk rows. */
export interface ClipExtent { minCy: number; maxCy: number }

/** An edit whose fold has not run yet — persisted so a crash inside the debounce can replay it. */
export interface PendingEdit { cx: number; cy: number; cz: number; box: VoxelBox }

export interface LodEditLayerDeps {
  /** Deepest level the cascade writes (default MAX_PACKABLE_LOD). */
  maxLod?: number;
  /** Milliseconds to coalesce edits before folding. */
  debounceMs: number;
  /** Live lod-0 voxels for a chunk — resident copy, else the persisted one, else null. */
  fineChunkData(cx: number, cy: number, cz: number): Promise<Uint32Array | null>;
  /**
   * A cascade finished: the coarse chunks whose layer changed (every level, resident or not) and the
   * column clips it WIDENED. The clips matter to the owner in the same breath as the layers — a
   * clip that only reaches its reader on the next column probe leaves the build's top row classified
   * sky for the rest of the session, so the layer is correct and nothing ever fetches the chunk.
   */
  onLayersChanged(touched: LodEditTouch[], clips: LodEditBatch['clips']): void;
  /** Persist a whole cascade's output in one transaction (fire-and-forget). */
  persist(batch: LodEditBatch): void;
  /** Persist the not-yet-folded edit boxes, or clear them when the list is empty. */
  savePending(list: PendingEdit[]): void;
}

/** One cascade's writes: the layers it rewrote and the column clips it widened. */
export interface LodEditBatch {
  layers: Array<{ cx: number; cy: number; cz: number; lod: number; idx: Uint16Array; words: Uint32Array }>;
  clips: Array<{ cx: number; cz: number; lod: number; minCy: number; maxCy: number }>;
}

/** A chunk being folded into during one level of a cascade. */
interface LevelChunk {
  cx: number; cy: number; cz: number;
  words: EditLayer;
  sink: FoldSink;
  box: VoxelBox | null;
  lowered: boolean;
}

/** Did rewriting `prev` as `next` drop this voxel's floor? (See LodEditTouch.) */
function lowersFloor(prev: number, next: number): boolean {
  if (((next >>> WEIGHT_SHIFT) & WEIGHT_MASK) < ((prev >>> WEIGHT_SHIFT) & WEIGHT_MASK)) return true;
  return (next & LAYER_MAY_LOWER) !== 0 && (prev & LAYER_MAY_LOWER) === 0;
}

export class LodEditLayers {
  private readonly layers = new Map<number, EditLayer>();
  private readonly clips = new Map<number, ClipExtent>();
  /** Edited lod-0 chunks awaiting a fold, keyed packChunkKey → union of their edit boxes. */
  private readonly pending = new Map<number, VoxelBox>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private flushing = false;
  private reflushWanted = false;
  private readonly maxLod: number;

  constructor(private readonly deps: LodEditLayerDeps) {
    this.maxLod = deps.maxLod ?? MAX_PACKABLE_LOD;
  }

  // ============== Read side (the streaming hot path) ==============

  /** Does this coarse chunk carry player edits? One Map probe — the ingest path's fast reject. */
  has(cx: number, cy: number, cz: number, lod: number): boolean {
    return this.layers.has(packChunkKeyLod(cx, cy, cz, lod));
  }

  /**
   * Merge this chunk's layer into freshly generated coarse data — geometry, material and the light
   * that came with it. A floor everywhere except inside a carve (see foldDense.mergeLayerWord), so
   * terrain can never be eroded by the fine/coarse weight-convention gap.
   *
   * Call this BEFORE the caller's relight (so the relight sees the build's geometry and its emitter),
   * then `applyLight` after it.
   * @returns true when any voxel changed.
   */
  apply(data: Uint32Array, cx: number, cy: number, cz: number, lod: number): boolean {
    const layer = this.layers.get(packChunkKeyLod(cx, cy, cz, lod));
    if (!layer) return false;
    let changed = false;
    for (const [i, word] of layer) {
      const merged = mergeLayerWord(data[i], word);
      if (merged !== data[i]) { data[i] = merged; changed = true; }
    }
    return changed;
  }

  /**
   * Raise this chunk's light to the folded lod-0 values, per field, leaving geometry alone — the
   * second half of the light model (see foldDense.raiseLayerLight). Runs AFTER the caller's relight.
   */
  applyLight(data: Uint32Array, cx: number, cy: number, cz: number, lod: number): boolean {
    const layer = this.layers.get(packChunkKeyLod(cx, cy, cz, lod));
    if (!layer) return false;
    let changed = false;
    for (const [i, word] of layer) {
      const lit = raiseLayerLight(data[i], word);
      if (lit !== data[i]) { data[i] = lit; changed = true; }
    }
    return changed;
  }

  /** The persisted vertical-clip widening for a coarse column, if it has one. */
  clipFor(cx: number, cz: number, lod: number): ClipExtent | undefined {
    return this.clips.get(packColumn(cx, cz, lod));
  }

  // ============== Write side ==============

  /**
   * Record an edited lod-0 chunk and the box the edit touched (local voxel bounds, inclusive), and
   * arm the debounce. Safe to call repeatedly — boxes union, and the fold reads the chunk's CURRENT
   * data whenever it eventually runs.
   */
  noteEdit(cx: number, cy: number, cz: number, box: VoxelBox): void {
    if (isEmptyBox(box)) return;
    const key = packChunkKey(cx, cy, cz);
    const prev = this.pending.get(key);
    // COPIED, never aliased. The caller's box is a scratch object reused for every chunk of an
    // operation (and for every operation after it), while a pending box has to survive the whole
    // debounce — holding the reference silently rewrote earlier chunks' boxes to the newest edit's
    // bounds, so a small multi-chunk build folded the wrong region and vanished at range.
    this.pending.set(key, prev ? unionBox(prev, box) : { ...box });
    this.deps.savePending(this.pendingList());
    if (this.timer === null) {
      this.timer = setTimeout(() => { this.timer = null; void this.flush(); }, this.deps.debounceMs);
    }
  }

  /** Anything waiting to be folded? */
  get hasPendingEdits(): boolean {
    return this.pending.size > 0;
  }

  /**
   * Fold every pending edit up the hierarchy now. Called by the debounce, and directly whenever the
   * far view is about to matter (a base-LOD step) or the session is ending (world switch, unload).
   */
  async flush(): Promise<void> {
    if (this.flushing) { this.reflushWanted = true; return; }
    if (this.timer !== null) { clearTimeout(this.timer); this.timer = null; }
    if (this.pending.size === 0) return;

    this.flushing = true;
    try {
      await this.runCascade();
    } finally {
      this.flushing = false;
    }
    if (this.reflushWanted) {
      this.reflushWanted = false;
      await this.flush();
    }
  }

  private async runCascade(): Promise<void> {
    // Every await happens HERE, before a single voxel moves: the cascade itself must be one
    // synchronous run, or a second edit could interleave against half-written layers.
    const edits: Array<{ cx: number; cy: number; cz: number; box: VoxelBox; data: Uint32Array }> = [];
    for (const [key, box] of [...this.pending]) {
      const [cx, cy, cz] = unpackChunkKeyTuple(key);
      const data = await this.deps.fineChunkData(cx, cy, cz);
      // Unreadable (evicted mid-flight, or a failed IDB read): leave it pending — its marker is
      // still persisted, so the next flush or the next session's replay picks it up.
      if (!data) continue;
      this.pending.delete(key);
      edits.push({ cx, cy, cz, box, data });
    }
    if (edits.length === 0) return;

    const touched: LodEditTouch[] = [];
    const dirtyLayers = new Set<number>();
    const dirtyClips = new Set<number>();
    /** Layers this cascade created; pruned below if the fold never wrote into them. */
    const created: number[] = [];

    // Level 0 → 1 is the only step that reads a real chunk array (fully known, hence null).
    let level = new Map<number, LevelChunk>();
    for (const e of edits) {
      const target = this.levelChunk(level, e.cx >> 1, e.cy >> 1, e.cz >> 1, 1, created);
      const written = foldOctant(
        (i) => e.data[i], null, padBox(e.box, 2), e.cx & 1, e.cy & 1, e.cz & 1, target.sink,
      );
      if (written) target.box = target.box ? unionBox(target.box, written) : written;
    }

    for (let lod = 1; lod <= this.maxLod; lod++) {
      this.finalizeLevel(level, lod, touched, dirtyLayers, dirtyClips);
      if (lod === this.maxLod) break;

      const next = new Map<number, LevelChunk>();
      for (const lc of level.values()) {
        if (!lc.box) continue;
        const target = this.levelChunk(next, lc.cx >> 1, lc.cy >> 1, lc.cz >> 1, lod + 1, created);
        const written = foldOctant(
          (i) => lc.words.get(i) ?? 0, (i) => lc.words.has(i), padBox(lc.box, 2),
          lc.cx & 1, lc.cy & 1, lc.cz & 1, target.sink,
        );
        if (written) target.box = target.box ? unionBox(target.box, written) : written;
      }
      if (next.size === 0) break;
      level = next;
    }

    // A fold stops climbing where the edited region no longer fills whole groups (that ceiling is
    // the point — see E2), so the last level attempted always allocates a layer it never writes.
    // An empty layer left behind would make `has` lie and mark resident chunks stale for nothing.
    for (const key of created) {
      const layer = this.layers.get(key);
      if (layer && layer.size === 0) this.layers.delete(key);
    }

    const batch: LodEditBatch = { layers: [], clips: [] };
    for (const key of dirtyLayers) {
      const layer = this.layers.get(key);
      if (!layer) continue;
      const { cx, cy, cz, lod } = unpackChunkKeyLod(key);
      batch.layers.push({ cx, cy, cz, lod, ...serializeLayer(layer) });
    }
    for (const key of dirtyClips) {
      const clip = this.clips.get(key);
      if (!clip) continue;
      const { cx, cz, lod } = unpackColumn(key);
      batch.clips.push({ cx, cz, lod, minCy: clip.minCy, maxCy: clip.maxCy });
    }
    this.deps.persist(batch);
    this.deps.savePending(this.pendingList());
    if (touched.length > 0) this.deps.onLayersChanged(touched, batch.clips);
  }

  /**
   * Resolve the may-lower flags for everything this level wrote, then record it. The erosion runs
   * AFTER every child of the level has folded, so a hole spanning several child chunks (or several
   * coarse chunks) is eroded against its real neighbourhood rather than against the octant seam.
   */
  private finalizeLevel(
    level: Map<number, LevelChunk>, lod: number,
    touched: LodEditTouch[], dirtyLayers: Set<number>, dirtyClips: Set<number>,
  ): void {
    for (const lc of level.values()) {
      if (!lc.box) continue;
      const { cx, cy, cz } = lc;
      erodeMayLower(lc.box, lc.sink, (lx, ly, lz) => {
        let ncx = cx, ncy = cy, ncz = cz;
        if (lx < 0) { ncx--; lx += CHUNK_SIZE; } else if (lx >= CHUNK_SIZE) { ncx++; lx -= CHUNK_SIZE; }
        if (ly < 0) { ncy--; ly += CHUNK_SIZE; } else if (ly >= CHUNK_SIZE) { ncy++; ly -= CHUNK_SIZE; }
        if (lz < 0) { ncz--; lz += CHUNK_SIZE; } else if (lz >= CHUNK_SIZE) { ncz++; lz -= CHUNK_SIZE; }
        const words = (ncx === cx && ncy === cy && ncz === cz)
          ? lc.words
          : this.layers.get(packChunkKeyLod(ncx, ncy, ncz, lod));
        const w = words?.get(voxelIndex(lx, ly, lz));
        return w === undefined ? -1 : w;
      });

      const key = packChunkKeyLod(cx, cy, cz, lod);
      dirtyLayers.add(key);
      touched.push({ cx, cy, cz, lod, lowered: lc.lowered });

      // THE CLIP MUST GROW WITH THE BUILD. A coarse column's vertical clip comes from the terrain
      // height, so a row holding nothing but a player's tower is classified sky, never requested,
      // and its layer never applies — the build is invisible AND the coverage walk counts the volume
      // represented-without-drawing (the canopy-band hole shape). Widen only, never narrow: an
      // over-wide clip costs one request that meshes empty, a narrow one is a hole.
      const colKey = packColumn(cx, cz, lod);
      const clip = this.clips.get(colKey);
      if (!clip) {
        this.clips.set(colKey, { minCy: cy, maxCy: cy });
        dirtyClips.add(colKey);
      } else if (cy < clip.minCy || cy > clip.maxCy) {
        clip.minCy = Math.min(clip.minCy, cy);
        clip.maxCy = Math.max(clip.maxCy, cy);
        dirtyClips.add(colKey);
      }
    }
  }

  /** The level's entry for a chunk, folding straight into that chunk's STORED layer. */
  private levelChunk(
    map: Map<number, LevelChunk>, cx: number, cy: number, cz: number, lod: number, created: number[],
  ): LevelChunk {
    const key = packChunkKeyLod(cx, cy, cz, lod);
    const existing = map.get(key);
    if (existing) return existing;
    let words = this.layers.get(key);
    if (!words) {
      words = new Map<number, number>();
      this.layers.set(key, words);
      created.push(key);
    }
    const store = words;
    const lc: LevelChunk = {
      cx, cy, cz, words: store, box: null, lowered: false,
      sink: {
        get: (i) => store.get(i) ?? -1,
        set: (i, w) => {
          const prev = store.get(i);
          if (prev !== undefined && w !== prev && lowersFloor(prev, w)) lc.lowered = true;
          store.set(i, w);
        },
      },
    };
    map.set(key, lc);
    return lc;
  }

  private pendingList(): PendingEdit[] {
    const out: PendingEdit[] = [];
    for (const [key, box] of this.pending) {
      const [cx, cy, cz] = unpackChunkKeyTuple(key);
      out.push({ cx, cy, cz, box });
    }
    return out;
  }

  // ============== Lifecycle ==============

  /** Install the persisted state for a world (called once, after the IDB preload). */
  hydrate(
    layers: Array<{ cx: number; cy: number; cz: number; lod: number; idx: Uint16Array; words: Uint32Array }>,
    clips: Array<{ cx: number; cz: number; lod: number; minCy: number; maxCy: number }>,
    pending: PendingEdit[],
  ): void {
    for (const rec of layers) {
      const map = new Map<number, number>();
      for (let i = 0; i < rec.idx.length; i++) map.set(rec.idx[i], rec.words[i]);
      this.layers.set(packChunkKeyLod(rec.cx, rec.cy, rec.cz, rec.lod), map);
    }
    for (const c of clips) {
      this.clips.set(packColumn(c.cx, c.cz, c.lod), { minCy: c.minCy, maxCy: c.maxCy });
    }
    // A marker that survived a reload is an edit whose fold never ran. Replaying it is free of
    // consequence even when it DID run — refolding a box is idempotent.
    for (const p of pending) this.pending.set(packChunkKey(p.cx, p.cy, p.cz), p.box);
    if (this.pending.size > 0 && this.timer === null) {
      this.timer = setTimeout(() => { this.timer = null; void this.flush(); }, this.deps.debounceMs);
    }
  }

  /** Drop everything (world switch). Pending edits are abandoned — their markers outlive them. */
  clear(): void {
    if (this.timer !== null) { clearTimeout(this.timer); this.timer = null; }
    this.layers.clear();
    this.clips.clear();
    this.pending.clear();
  }

  /** DEV/test introspection: the layer for one coarse chunk, if any. */
  layerAt(cx: number, cy: number, cz: number, lod: number): EditLayer | undefined {
    return this.layers.get(packChunkKeyLod(cx, cy, cz, lod));
  }

  /** DEV/test introspection: every coarse chunk that carries edits. */
  keys(): ChunkCoordLod[] {
    return [...this.layers.keys()].map(unpackChunkKeyLod);
  }

  /** DEV/test introspection: how many coarse chunks carry edits, and how many voxels in total. */
  stats(): { chunks: number; voxels: number } {
    let voxels = 0;
    for (const l of this.layers.values()) voxels += l.size;
    return { chunks: this.layers.size, voxels };
  }
}

/** Sparse layer → two typed arrays, the persisted form (6 bytes per voxel). */
export function serializeLayer(layer: EditLayer): { idx: Uint16Array; words: Uint32Array } {
  const n = layer.size;
  const idx = new Uint16Array(n);
  const words = new Uint32Array(n);
  let k = 0;
  for (const [i, w] of layer) { idx[k] = i; words[k] = w; k++; }
  return { idx, words };
}

/** Strip the may-lower flag — for callers comparing a layer word against chunk data. */
export function layerWordValue(word: number): number {
  return word & ~LAYER_MAY_LOWER;
}
